<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { AlertCircle, BookOpenText, CheckCircle2, FileText, FlaskConical, Plus, RefreshCw, Save, Search, Trash2, Upload, X } from "lucide-vue-next";
import { api } from "../services/api";
import type { KnowledgeBase, KnowledgeHit, KnowledgeRetrievalMode } from "../types";

type KnowledgeBaseDraft = {
  id?: string;
  slug: string;
  name: string;
  description: string;
  retrievalMode: KnowledgeRetrievalMode;
  embeddingModel: string;
  chunkSize: number;
  chunkOverlap: number;
  topK: number;
  minScore: number;
  maxContextChars: number;
  isEnabled: boolean;
};

const bases = ref<KnowledgeBase[]>([]);
const selectedId = ref("");
const draft = ref<KnowledgeBaseDraft>(newDraft());
const loading = ref(false);
const saving = ref(false);
const reindexing = ref(false);
const error = ref("");
const success = ref("");
const importOpen = ref(false);
const importBusy = ref(false);
const fileReading = ref(false);
const importError = ref("");
const documentDraft = ref({ title: "", sourceUri: "", content: "" });
const searchQuery = ref("");
const searchBusy = ref(false);
const searchHits = ref<KnowledgeHit[]>([]);

const selectedBase = computed(() => bases.value.find((base) => base.id === selectedId.value));

function newDraft(): KnowledgeBaseDraft {
  return {
    slug: "",
    name: "",
    description: "",
    retrievalMode: "KEYWORD",
    embeddingModel: "",
    chunkSize: 800,
    chunkOverlap: 120,
    topK: 4,
    minScore: 0.2,
    maxContextChars: 6000,
    isEnabled: true,
  };
}

function selectBase(base: KnowledgeBase): void {
  selectedId.value = base.id;
  draft.value = {
    id: base.id,
    slug: base.slug,
    name: base.name,
    description: base.description ?? "",
    retrievalMode: base.retrievalMode,
    embeddingModel: base.embeddingModel ?? "",
    chunkSize: base.chunkSize,
    chunkOverlap: base.chunkOverlap,
    topK: base.topK,
    minScore: base.minScore,
    maxContextChars: base.maxContextChars,
    isEnabled: base.isEnabled,
  };
  error.value = "";
  success.value = "";
  searchHits.value = [];
}

function startCreate(): void {
  selectedId.value = "";
  draft.value = newDraft();
  error.value = "";
  success.value = "";
  searchHits.value = [];
}

async function load(preferredId?: string): Promise<void> {
  loading.value = true;
  try {
    bases.value = await api.adminKnowledgeBases();
    const id = preferredId ?? selectedId.value;
    const selected = bases.value.find((base) => base.id === id) ?? bases.value[0];
    if (selected) selectBase(selected);
    else startCreate();
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "知识库加载失败";
  } finally {
    loading.value = false;
  }
}

function validateDraft(): boolean {
  error.value = "";
  if (!draft.value.name.trim()) error.value = "请填写知识库名称";
  else if (!draft.value.slug.trim()) error.value = "请填写知识库标识";
  else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(draft.value.slug.trim())) error.value = "知识库标识只能使用小写字母、数字和短横线";
  else if (draft.value.chunkOverlap >= draft.value.chunkSize) error.value = "分块重叠必须小于分块大小";
  else if (draft.value.retrievalMode === "HYBRID" && !draft.value.embeddingModel.trim()) error.value = "混合检索必须填写 Embedding 模型 ID 或向量化接入点 ID";
  return !error.value;
}

async function save(): Promise<void> {
  if (!validateDraft()) return;
  const creating = !draft.value.id;
  saving.value = true;
  success.value = "";
  try {
    const saved = await api.saveKnowledgeBase({
      id: draft.value.id,
      slug: draft.value.slug.trim(),
      name: draft.value.name.trim(),
      description: draft.value.description.trim() || null,
      retrievalMode: draft.value.retrievalMode,
      embeddingModel: draft.value.embeddingModel.trim() || null,
      chunkSize: Number(draft.value.chunkSize),
      chunkOverlap: Number(draft.value.chunkOverlap),
      topK: Number(draft.value.topK),
      minScore: Number(draft.value.minScore),
      maxContextChars: Number(draft.value.maxContextChars),
      isEnabled: draft.value.isEnabled,
    });
    await load(saved.id);
    success.value = creating ? "知识库已创建，可以开始导入文档" : "知识库设置已保存；分块或模型有变化时请重新索引";
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "知识库保存失败";
  } finally {
    saving.value = false;
  }
}

async function removeBase(): Promise<void> {
  if (!selectedId.value || !window.confirm("确定删除该知识库及其全部文档和分块？")) return;
  try {
    await api.deleteKnowledgeBase(selectedId.value);
    selectedId.value = "";
    await load();
    success.value = "知识库已删除";
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "知识库删除失败";
  }
}

function openImport(): void {
  documentDraft.value = { title: "", sourceUri: "", content: "" };
  importError.value = "";
  importOpen.value = true;
}

function normalizeImportedText(content: string): string {
  return content.replace(/\r\n?/g, "\n").replace(/\n{4,}/g, "\n\n\n").trim();
}

async function readDocumentFile(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = "";
  if (!file) return;
  const extension = file.name.toLocaleLowerCase().match(/\.[^.]+$/)?.[0] ?? "";
  if (extension === ".doc") {
    importError.value = "旧版 .doc 格式无法安全解析，请在 Word 中另存为 .docx 后导入";
    return;
  }
  const isDocx = extension === ".docx";
  const isText = [".txt", ".md", ".markdown", ".json", ".csv"].includes(extension);
  if (!isDocx && !isText) {
    importError.value = "仅支持 DOCX、TXT、Markdown、JSON 和 CSV 文件";
    return;
  }
  const maxFileBytes = isDocx ? 10_000_000 : 1_000_000;
  if (file.size > maxFileBytes) {
    importError.value = isDocx ? "DOCX 文件不能超过 10 MB" : "文本文件不能超过 1 MB";
    return;
  }
  fileReading.value = true;
  importError.value = "";
  try {
    documentDraft.value.title = documentDraft.value.title || file.name.replace(/\.[^.]+$/, "");
    documentDraft.value.sourceUri = documentDraft.value.sourceUri || file.name;
    let content: string;
    if (isDocx) {
      const mammoth = (await import("mammoth")).default;
      const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
      content = result.value;
    } else {
      content = await file.text();
    }
    content = normalizeImportedText(content);
    if (!content) throw new Error("文档中没有可提取的文字内容");
    if (content.length > 1_000_000) throw new Error("提取后的文字超过 100 万字符，请拆分文档后导入");
    documentDraft.value.content = content;
  } catch (reason) {
    importError.value = reason instanceof Error ? reason.message : "文件读取失败";
  } finally {
    fileReading.value = false;
  }
}

async function importDocument(): Promise<void> {
  if (!selectedId.value) return;
  if (!documentDraft.value.title.trim() || !documentDraft.value.content.trim()) {
    importError.value = "请填写文档标题和内容";
    return;
  }
  importBusy.value = true;
  importError.value = "";
  try {
    await api.addKnowledgeDocument(selectedId.value, {
      title: documentDraft.value.title.trim(),
      sourceUri: documentDraft.value.sourceUri.trim() || null,
      content: documentDraft.value.content.trim(),
    });
    importOpen.value = false;
    await load(selectedId.value);
    success.value = "文档已导入并完成索引";
  } catch (reason) {
    importError.value = reason instanceof Error ? reason.message : "文档导入失败";
  } finally {
    importBusy.value = false;
  }
}

async function removeDocument(id: string): Promise<void> {
  if (!window.confirm("确定删除这份文档？")) return;
  try {
    await api.deleteKnowledgeDocument(id);
    await load(selectedId.value);
    success.value = "文档已删除";
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "文档删除失败";
  }
}

async function reindex(): Promise<void> {
  if (!selectedId.value) return;
  reindexing.value = true;
  error.value = "";
  success.value = "";
  try {
    const result = await api.reindexKnowledgeBase(selectedId.value);
    await load(selectedId.value);
    success.value = `重新索引完成：成功 ${result.indexed} 份，失败 ${result.failed} 份`;
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "重新索引失败";
  } finally {
    reindexing.value = false;
  }
}

async function testSearch(): Promise<void> {
  if (!selectedId.value || !searchQuery.value.trim()) return;
  searchBusy.value = true;
  error.value = "";
  try {
    searchHits.value = await api.testKnowledgeSearch(selectedId.value, searchQuery.value.trim());
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "检索测试失败";
  } finally {
    searchBusy.value = false;
  }
}

function formatTime(value?: string | null): string {
  return value ? new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(value)) : "未索引";
}

onMounted(() => void load());
</script>

<template>
  <div class="knowledge-layout">
    <aside class="surface-section knowledge-list-panel">
      <div class="section-header">
        <div><span class="eyebrow">RAG</span><h2>知识库</h2></div>
        <button class="icon-button compact" type="button" title="新建知识库" @click="startCreate"><Plus :size="17" /></button>
      </div>
      <button v-for="base in bases" :key="base.id" type="button" :class="['knowledge-list-item', { active: selectedId === base.id }]" @click="selectBase(base)">
        <span :class="['status-dot', base.isEnabled ? 'ok' : 'bad']" />
        <div><strong>{{ base.name }}</strong><small>{{ base.documents.length }} 份文档 · {{ base.chunkCount }} 个分块</small></div>
      </button>
      <div v-if="!bases.length && !loading" class="empty-state">暂无知识库</div>
    </aside>

    <main class="knowledge-main">
      <form class="surface-section knowledge-settings" novalidate @submit.prevent="save">
        <div class="section-header">
          <div><span class="eyebrow">{{ selectedId ? "配置" : "新建" }}</span><h2>{{ draft.name || "知识库设置" }}</h2></div>
          <div class="header-command-group">
            <button v-if="selectedId" class="icon-button danger-icon" type="button" title="删除知识库" @click="removeBase"><Trash2 :size="17" /></button>
            <button class="button primary" type="submit" :disabled="saving"><Save :size="16" />{{ saving ? "保存中" : "保存" }}</button>
          </div>
        </div>
        <p v-if="success" class="form-success action-feedback" role="status"><CheckCircle2 :size="16" />{{ success }}</p>
        <p v-if="error" class="form-error action-feedback" role="alert"><AlertCircle :size="16" />{{ error }}</p>
        <div class="knowledge-settings-grid">
          <label class="form-field"><span>名称</span><input v-model="draft.name" maxlength="120" placeholder="产品使用说明" /></label>
          <label class="form-field"><span>标识</span><input v-model="draft.slug" maxlength="80" placeholder="product-manual" :disabled="Boolean(selectedId)" /></label>
          <label class="form-field span-2"><span>说明</span><input v-model="draft.description" maxlength="2000" /></label>
          <label class="form-field"><span>检索模式</span><select v-model="draft.retrievalMode"><option value="KEYWORD">关键词检索</option><option value="HYBRID">关键词 + Embedding</option></select></label>
          <label class="form-field">
            <span>Embedding 模型 ID / 接入点 ID</span>
            <input v-model="draft.embeddingModel" :disabled="draft.retrievalMode !== 'HYBRID'" placeholder="doubao-embedding-vision-251215" />
            <small v-if="draft.retrievalMode === 'HYBRID'">推荐填写 doubao-embedding-vision-251215；ep-... 必须属于向量化模型，不能使用对话模型接入点。</small>
          </label>
          <label class="form-field"><span>分块大小</span><input v-model.number="draft.chunkSize" type="number" min="200" max="4000" /></label>
          <label class="form-field"><span>分块重叠</span><input v-model.number="draft.chunkOverlap" type="number" min="0" max="1000" /></label>
          <label class="form-field"><span>返回分块数 Top K</span><input v-model.number="draft.topK" type="number" min="1" max="12" /></label>
          <label class="form-field"><span>最低匹配分</span><input v-model.number="draft.minScore" type="number" min="0" max="1" step="0.01" /></label>
          <label class="form-field"><span>最大上下文字符</span><input v-model.number="draft.maxContextChars" type="number" min="1000" max="20000" step="500" /></label>
          <label class="toggle-control knowledge-enabled"><input v-model="draft.isEnabled" type="checkbox" /><span class="toggle-track" /><span>启用知识库</span></label>
        </div>
      </form>

      <section v-if="selectedBase" class="surface-section knowledge-documents">
        <div class="section-header">
          <div><span class="eyebrow">DOCUMENTS</span><h2>文档与索引</h2></div>
          <div class="header-command-group">
            <button class="button secondary" type="button" :disabled="reindexing || !selectedBase.documents.length" @click="reindex"><RefreshCw :size="16" :class="{ spinning: reindexing }" />重新索引</button>
            <button class="button primary" type="button" @click="openImport"><Upload :size="16" />导入文档</button>
          </div>
        </div>
        <div class="knowledge-document-list">
          <div v-for="document in selectedBase.documents" :key="document.id" class="knowledge-document-row">
            <span class="document-icon"><FileText :size="17" /></span>
            <div><strong>{{ document.title }}</strong><small>{{ document.chunkCount }} 个分块 · {{ formatTime(document.lastIndexedAt) }}</small><p v-if="document.errorMessage">{{ document.errorMessage }}</p></div>
            <button class="icon-button compact danger-icon" type="button" title="删除文档" @click="removeDocument(document.id)"><Trash2 :size="15" /></button>
          </div>
          <div v-if="!selectedBase.documents.length" class="empty-state">暂无文档，可导入 DOCX、TXT、Markdown、JSON 或 CSV</div>
        </div>
      </section>

      <section v-if="selectedBase" class="surface-section knowledge-test">
        <div class="section-header"><div><span class="eyebrow">RETRIEVAL TEST</span><h2>检索测试</h2></div><FlaskConical :size="18" /></div>
        <form class="knowledge-search-form" @submit.prevent="testSearch">
          <input v-model="searchQuery" placeholder="输入用户可能提出的问题" />
          <button class="button secondary" type="submit" :disabled="searchBusy || !searchQuery.trim()"><Search :size="16" />{{ searchBusy ? "检索中" : "检索" }}</button>
        </form>
        <div class="knowledge-hit-list">
          <article v-for="hit in searchHits" :key="`${hit.documentId}-${hit.position}`" class="knowledge-hit-row">
            <div><strong>{{ hit.documentTitle }}</strong><span>匹配分 {{ hit.score.toFixed(3) }}</span></div>
            <p>{{ hit.content }}</p>
          </article>
          <div v-if="searchQuery && !searchBusy && !searchHits.length" class="empty-state compact-empty">没有达到最低匹配分的内容</div>
        </div>
      </section>
    </main>

    <div v-if="importOpen" class="modal-backdrop" @click.self="importOpen = false">
      <form class="modal-panel knowledge-import-modal" @submit.prevent="importDocument">
        <div class="modal-header">
          <div><span class="eyebrow">IMPORT</span><h2>导入知识文档</h2></div>
          <button class="icon-button" type="button" title="关闭" @click="importOpen = false"><X :size="18" /></button>
        </div>
        <div class="form-stack">
          <label class="form-field"><span>文档标题</span><input v-model="documentDraft.title" maxlength="200" /></label>
          <label class="form-field"><span>来源地址（可选）</span><input v-model="documentDraft.sourceUri" maxlength="1000" placeholder="https://... 或内部文档编号" /></label>
          <label class="button secondary file-import-button">
            <Upload :size="16" />{{ fileReading ? "正在读取" : "选择文档" }}
            <input type="file" accept=".docx,.doc,.txt,.md,.markdown,.json,.csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,application/json,text/csv" :disabled="fileReading || importBusy" @change="readDocumentFile" />
          </label>
          <label class="form-field"><span>文档内容</span><textarea v-model="documentDraft.content" rows="14" maxlength="1000000" placeholder="粘贴文字，或导入 DOCX / UTF-8 文本" /></label>
        </div>
        <p v-if="importError" class="form-error" role="alert"><AlertCircle :size="16" />{{ importError }}</p>
        <div class="modal-actions">
          <button class="button secondary" type="button" :disabled="importBusy" @click="importOpen = false">取消</button>
          <button class="button primary" type="submit" :disabled="importBusy || fileReading"><BookOpenText :size="16" />{{ importBusy ? "索引中" : "导入并索引" }}</button>
        </div>
      </form>
    </div>
  </div>
</template>
